import { useQuery } from "@tanstack/react-query";

import {
  type BubbleGetBubblePermissionsVariables,
  type BubbleGetPendingApprovalsVariables,
  type EventGetPendingEventTimesVariables,
  fetchBubbleGetBubblePermissions as fetchBubbleGetBubblePermissionsGenerated,
  fetchBubbleGetPendingApprovals as fetchBubbleGetPendingApprovalsGenerated,
  fetchEventGetPendingEventTimes as fetchEventGetPendingEventTimesGenerated,
} from "../openapi/generated/CrowdcoursingApiComponents";
import { type UpdateBubblePermissionsRequest } from "../openapi/generated/CrowdcoursingApiSchemas";

export interface BubblePermissionsValuesDto {
  canCreateEvents: boolean;
  eventTimesRequireApproval: boolean;
  allowChildBubblesCanCreateEvents: boolean;
  allowChildBubblesEventTimesRequireApproval: boolean;
}

export interface BubblePermissionConstraintsDto {
  canCreateEvents: boolean;
  eventTimesRequireApproval: boolean;
}

export interface BubblePermissionsResponseDto {
  bubbleId: string;
  local: BubblePermissionsValuesDto;
  effective: BubblePermissionsValuesDto;
  constraints: BubblePermissionConstraintsDto;
}

/** Mirrors backend ApprovalType enum. */
export type ApprovalType = 0 | 1 | 2 | 3;

export interface PendingApprovalDto {
  bubbleId?: string;
  id: string;
  parentId: string;
  name: string;
  approvalType: ApprovalType;
  count: number;
}

export const bubblePermissionsQueryKey = ["Bubbles", "permissions"] as const;
export const bubblePendingApprovalsQueryKey = ["Bubbles", "pending-approvals"] as const;
export const eventPendingTimesQueryKey = ["Event", "pending-event-times"] as const;

export async function fetchBubbleGetBubblePermissions(
  variables: { pathParams: { bubbleId: string } },
  signal?: AbortSignal,
): Promise<BubblePermissionsResponseDto | null> {
  const result = await fetchBubbleGetBubblePermissionsGenerated(
    variables as BubbleGetBubblePermissionsVariables,
    signal,
  );
  return result as unknown as BubblePermissionsResponseDto | null;
}

export function useBubbleGetBubblePermissions(bubbleId: string | null | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    enabled: !!bubbleId && (options?.enabled ?? true),
    queryFn: ({ signal }) => fetchBubbleGetBubblePermissions({ pathParams: { bubbleId: bubbleId ?? "" } }, signal),
    queryKey: [...bubblePermissionsQueryKey, bubbleId],
    ...options,
  });
}

export async function fetchBubbleGetPendingApprovals(
  variables: { pathParams: { bubbleId: string } },
  signal?: AbortSignal,
): Promise<PendingApprovalDto[]> {
  const result = await fetchBubbleGetPendingApprovalsGenerated(variables as BubbleGetPendingApprovalsVariables, signal);
  return (result as unknown as PendingApprovalDto[] | null) ?? [];
}

export function useBubbleGetPendingApprovals(bubbleId: string | null | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    enabled: !!bubbleId && (options?.enabled ?? true),
    queryFn: ({ signal }) => fetchBubbleGetPendingApprovals({ pathParams: { bubbleId: bubbleId ?? "" } }, signal),
    queryKey: [...bubblePendingApprovalsQueryKey, bubbleId],
    ...options,
  });
}

export interface PendingMeetingTimeDto {
  id?: string;
  eventId?: string;
  eventName?: string | null;
  seriesId?: string | null;
  seriesName?: string | null;
  beginMeetingTime?: string;
  endMeetingTime?: string;
  capacity?: number;
  approvalStatus?: number;
  approvedAt?: string | null;
}

export async function fetchEventGetPendingEventTimes(
  variables: { pathParams: { eventId: string } },
  signal?: AbortSignal,
): Promise<PendingMeetingTimeDto[]> {
  const result = await fetchEventGetPendingEventTimesGenerated(variables as EventGetPendingEventTimesVariables, signal);
  return (result as unknown as PendingMeetingTimeDto[] | null) ?? [];
}

export function useEventGetPendingEventTimesTyped(eventId: string | null | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    enabled: !!eventId && (options?.enabled ?? true),
    queryFn: ({ signal }) => fetchEventGetPendingEventTimes({ pathParams: { eventId: eventId ?? "" } }, signal),
    queryKey: [...eventPendingTimesQueryKey, eventId],
    ...options,
  });
}

export type { UpdateBubblePermissionsRequest };
